import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache for resolved emails
const emailCache = new Map();

// Function to clear cache (for testing)
export const clearEmailCache = () => {
  emailCache.clear();
};

/**
 * Resolves user_ids to email addresses for SuperAdmin users
 * @param {string[]} userIds - Array of user IDs to resolve
 * @returns {Promise<Map<string, string|null>>} Map of user_id to email (null for 404 errors)
 * @throws {Error} For 500 server errors or network failures
 */
export const resolveEmails = async (userIds) => {
  if (!userIds || userIds.length === 0) {
    return new Map();
  }

  try {
    // Filter out already cached user_ids
    const uncachedUserIds = userIds.filter(userId => !emailCache.has(userId));
    
    // If all user_ids are cached, return cached results
    if (uncachedUserIds.length === 0) {
      const cachedMap = new Map();
      userIds.forEach(userId => {
        cachedMap.set(userId, emailCache.get(userId));
      });
      return cachedMap;
    }

        // Batch fetch uncached emails in parallel
        const promises = uncachedUserIds.map(async (userId) => {
          try {
            const response = await axios.get(`${API_BASE}/admin/analytics/user-email/${userId}`);
            return { userId, email: response.data.email };
          } catch (error) {
            if (error.response?.status === 404) {
              // Orphaned user - return null
              return { userId, email: null };
            } else {
              // Re-throw other errors (500, network, etc.)
              throw error;
            }
          }
        });

    const results = await Promise.all(promises);
    
    // Cache the results
    results.forEach(({ userId, email }) => {
      emailCache.set(userId, email);
    });

    // Return combined results (cached + newly fetched)
    const emailMap = new Map();
    userIds.forEach(userId => {
      emailMap.set(userId, emailCache.get(userId));
    });

    return emailMap;
  } catch (error) {
    // Re-throw with original error details
    throw new Error(error.response?.data?.detail || error.message);
  }
};
